/**
 * Sprint-5 → Phase 1 Item 5 — Public remote consent page.
 *
 * Subjects open this from the email or SMS link. The page has two
 * stages under the hood:
 *
 *   1. "Request my PIN" — subject taps the button; BE issues a PIN and
 *      dispatches it to whichever contact channel(s) the site has on
 *      file for them. Any prior active PIN is invalidated.
 *   2. Enter the 4-digit PIN → verifyPin returns the DocuSeal embed URL.
 *
 * Prior to Item 5 the coordinator dispatched BOTH the link and the PIN
 * up-front (send-pin), which put the PIN in the coordinator's outbound
 * audit trail. Now the OTP only leaves the server when the subject
 * actively requests it — a cleaner Part-11 separation between "who
 * sent the invite" and "who verified identity".
 *
 * No JWT required — this is intentionally a public route.
 *
 * URL shape: /consent/remote?cr=<consent_record_id>
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { consent } from '@/api/client';
import { SigningProgress } from '@/pages/subjects/onboarding/SigningProgress';

const PIN_LEN = 4;

export function RemoteConsentPage() {
  const [params] = useSearchParams();
  const consentRecordId = params.get('cr') ?? '';

  const embedKey = `consent_embed_${consentRecordId}`;

  const [digits, setDigits] = useState<string[]>(Array(PIN_LEN).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Restore a stored embed URL so the subject can resume if they navigate back
  // without finishing. Uses localStorage so it survives across tabs.
  const [embedUrl, setEmbedUrl] = useState<string | null>(
    () => (consentRecordId ? localStorage.getItem(embedKey) : null),
  );
  // true only after a fresh PIN verify — triggers auto-redirect vs. resume button.
  const [autoRedirect, setAutoRedirect] = useState(false);
  // null = not checked yet, false = not signed, true = already signed
  const [alreadySigned, setAlreadySigned] = useState<boolean | null>(null);

  // Phase 1 Item 5 — the request-OTP state. Starts null (subject hasn't
  // asked yet). Once the OTP has been dispatched we store the masked
  // channels so we can tell the subject where to look.
  const [otpRequested, setOtpRequested] = useState<null | {
    email: string | null;
    sms: string | null;
    expires_at: string;
  }>(null);
  const [requestingOtp, setRequestingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  const pin = digits.join('');
  const ready = pin.length === PIN_LEN && /^\d+$/.test(pin) && !!consentRecordId;

  // On mount, check if consent is already signed (handles re-opening email link after signing).
  useEffect(() => {
    if (!consentRecordId || params.get('signed') === '1') return;
    consent.remoteStatus(consentRecordId)
      .then((r) => {
        if (r.signed) {
          localStorage.removeItem(embedKey);
          setAlreadySigned(true);
        } else {
          setAlreadySigned(false);
        }
      })
      .catch(() => setAlreadySigned(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentRecordId]);

  async function submit() {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await consent.verifyPin({ consent_record_id: consentRecordId, pin });
      if (!r.embed_url) {
        setError('Signing session not ready — contact the site coordinator.');
        setDigits(Array(PIN_LEN).fill(''));
        return;
      }
      localStorage.setItem(embedKey, r.embed_url);
      setAutoRedirect(true);
      setEmbedUrl(r.embed_url);
    } catch (e: unknown) {
      const err = e as { status?: number; code?: string; message?: string };
      if (err.status === 409) {
        localStorage.removeItem(embedKey);
        setAlreadySigned(true);
        return;
      }
      if (err.code === 'PIN_EXPIRED') {
        setError('Your PIN has expired. Contact the coordinator to send a new one.');
      } else if (err.code === 'PIN_CONSUMED') {
        setError('Your PIN has already been used and the signing form could not be retrieved. Please contact the site coordinator for assistance.');
      } else if (err.status === 423) {
        setError('Too many failed attempts. Contact the site coordinator for a new PIN.');
      } else {
        setError('Incorrect PIN. Please check the email and try again.');
      }
      setDigits(Array(PIN_LEN).fill(''));
    } finally {
      setSubmitting(false);
    }
  }

  async function requestOtp() {
    if (!consentRecordId || requestingOtp) return;
    setRequestingOtp(true);
    setOtpError(null);
    try {
      const r = await consent.requestOtp({ consent_record_id: consentRecordId });
      setOtpRequested({
        email: r.channels_masked.email,
        sms: r.channels_masked.sms,
        expires_at: r.expires_at,
      });
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string; body?: { retry_after_seconds?: number } };
      if (err.status === 429) {
        const retry = err.body?.retry_after_seconds ?? 60;
        setOtpError(`Too many attempts. Try again in about ${retry} seconds.`);
      } else if (err.status === 409) {
        // Already signed — flip the same state the mount check would have.
        localStorage.removeItem(embedKey);
        setAlreadySigned(true);
      } else {
        setOtpError(err.message || 'Could not send your PIN. Please contact the site coordinator.');
      }
    } finally {
      setRequestingOtp(false);
    }
  }

  useEffect(() => {
    // Auto-submit when the 4th digit lands.
    if (ready) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  useEffect(() => {
    if (embedUrl && autoRedirect) window.location.href = embedUrl;
  }, [embedUrl, autoRedirect]);

  if (!consentRecordId) {
    return (
      <Shell>
        <p className="text-sm text-ink-muted">
          This link is missing the consent reference. Open it from the email message exactly as it
          arrived.
        </p>
      </Shell>
    );
  }

  // Subject returned from DocuSeal after signing (?signed=1 set by completed_redirect_url).
  if (params.get('signed') === '1') {
    localStorage.removeItem(embedKey);
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'completed', sign: 'completed', confirmed: 'completed' }}
          labels={{ identify: 'Verified', sign: 'Sign', confirmed: 'Confirmed' }}
        />
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <ShieldCheck className="h-10 w-10 text-accent-600" />
          <p className="text-base font-semibold text-ink-primary">Consent form submitted</p>
          <p className="text-xs text-ink-muted">
            Thank you. Your signed consent has been recorded. You may close this window.
          </p>
        </div>
      </Shell>
    );
  }

  // Consent already signed — detected on load or via 409 on PIN submit.
  if (alreadySigned === true) {
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'completed', sign: 'completed', confirmed: 'completed' }}
          labels={{ identify: 'Verified', sign: 'Sign', confirmed: 'Confirmed' }}
        />
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <ShieldCheck className="h-10 w-10 text-accent-600" />
          <p className="text-base font-semibold text-ink-primary">Consent already submitted</p>
          <p className="text-xs text-ink-muted">
            Your consent form has already been signed. You may close this window.
          </p>
        </div>
      </Shell>
    );
  }

  // embedUrl set from a fresh PIN verify → auto-redirect in progress; show nothing.
  if (embedUrl && autoRedirect) return null;

  // embedUrl restored from sessionStorage → subject came back without finishing.
  if (embedUrl) {
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'completed', sign: 'active', confirmed: 'pending' }}
          labels={{ identify: 'Verified', sign: 'Sign', confirmed: 'Confirmed' }}
        />
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <p className="text-sm font-medium text-ink-primary">Your PIN has already been verified.</p>
          <p className="text-xs text-ink-muted">
            Click below to open the signing form and complete your consent.
          </p>
          <button
            type="button"
            onClick={() => { window.location.href = embedUrl; }}
            className="mt-2 w-full rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            Open signing form
          </button>
        </div>
      </Shell>
    );
  }

  // Still loading status check — show nothing yet to avoid PIN form flash.
  if (alreadySigned === null) return null;

  // Phase 1 Item 5 — before the PIN input, the subject requests the OTP.
  // Only after `otpRequested` is set (successful dispatch) do we show
  // the PIN entry surface.
  if (!otpRequested) {
    return (
      <Shell>
        <SigningProgress
          stages={{ identify: 'active', sign: 'pending', confirmed: 'pending' }}
          labels={{ identify: 'Verify', sign: 'Sign', confirmed: 'Confirmed' }}
        />
        <h1 className="mt-6 text-center text-base font-semibold text-ink-primary">
          Verify your identity
        </h1>
        <p className="mt-2 text-center text-xs text-ink-muted">
          We'll send a single-use 4-digit PIN to the email or phone number on file with your site.
          Tap the button below to receive it.
        </p>

        {otpError && (
          <p className="mt-4 text-center text-xs text-danger-600">{otpError}</p>
        )}

        <button
          type="button"
          onClick={requestOtp}
          disabled={requestingOtp}
          className="mt-6 w-full rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {requestingOtp ? 'Sending your PIN…' : 'Send my PIN'}
        </button>

        <p className="mt-6 text-center text-[11px] text-ink-muted">
          If you no longer have access to the contact details on file, please contact your site
          coordinator.
        </p>
      </Shell>
    );
  }

  const channels = [otpRequested.email, otpRequested.sms].filter(Boolean).join(' and ');

  return (
    <Shell>
      <SigningProgress
        stages={{ identify: 'active', sign: 'pending', confirmed: 'pending' }}
        labels={{ identify: 'Verify', sign: 'Sign', confirmed: 'Confirmed' }}
      />
      <div className="mt-6 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        <span className="font-semibold">Important:</span> Your PIN is single-use. Once entered, you
        will be taken directly to the signing form — please complete signing without navigating away.
      </div>
      <h1 className="mt-5 text-center text-base font-semibold text-ink-primary">Enter your PIN</h1>
      <p className="mt-1 text-center text-xs text-ink-muted">
        Enter the 4-digit PIN we just sent to {channels || 'your contact on file'}
      </p>

      <div className="mt-6 flex justify-center gap-2">
        {digits.map((d, i) => (
          <input
            key={i}
            id={`pin-${i}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            disabled={submitting}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(-1);
              setDigits((prev) => {
                const next = [...prev];
                next[i] = v;
                return next;
              });
              if (v && i < PIN_LEN - 1) {
                const nextEl = document.getElementById(`pin-${i + 1}`) as HTMLInputElement | null;
                nextEl?.focus();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                const prevEl = document.getElementById(`pin-${i - 1}`) as HTMLInputElement | null;
                prevEl?.focus();
              }
            }}
            className={`h-14 w-12 rounded border text-center text-2xl font-semibold ${
              error ? 'border-danger-400 text-danger-700' : 'border-surface-border text-ink-primary'
            }`}
          />
        ))}
      </div>

      {error && <p className="mt-3 text-center text-xs text-danger-600">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={!ready || submitting}
        className="mt-6 w-full rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
      >
        {submitting ? 'Verifying…' : 'Start signing'}
      </button>

      <p className="mt-6 text-center text-[11px] text-ink-muted">
        Didn't get a PIN? Reply to the email or contact the site coordinator listed in the link
        message.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-base">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6 text-center">
          <div className="text-lg font-semibold text-ink-primary">DreamTrials</div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">
            Informed Consent
          </div>
        </div>
        <div className="rounded border border-surface-border bg-surface-card p-6 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}
